import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Search, GraduationCap, Building2, Clock, X, Users, TrendingUp, CheckCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { coursesService } from '../services/courses';
import { organizationsService } from '../services/organizations';
import { supabase } from '../config/supabase';
import { Course, Organization } from '../types';
import { useAuth } from '../contexts/AuthContext';

const MINT  = '#2f9e5a';
const GOLD  = '#f39200';
const CORAL = '#af3216';

const formatDuration = (minutes?: number): string => {
  if (!minutes) return 'Durée non définie';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

export const Programs: React.FC = () => {
  const { profile } = useAuth();
  const isRestrictedUser = profile ? profile.role !== 'super_admin' : true;

  const [courses, setCourses] = useState<Course[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [viewingCourse, setViewingCourse] = useState<Course | null>(null);

  useEffect(() => { loadData(); }, [profile]);

  useEffect(() => {
    if (selectedOrgId) loadCoursesForOrg(selectedOrgId);
    else setCourses([]);
  }, [selectedOrgId]);

  const loadData = async () => {
    try {
      if (!profile) return;
      if (isRestrictedUser && profile.organizationId) {
        setSelectedOrgId(profile.organizationId);
        setLoading(false);
        return;
      }
      if (!isRestrictedUser) {
        const orgs = await organizationsService.getAll();
        setOrganizations(orgs);
        if (orgs.length > 0) setSelectedOrgId(orgs[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoursesForOrg = async (orgId: string) => {
    try {
      const data = await coursesService.getByOrganization(orgId);
      setCourses(data);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const filteredCourses = courses.filter(course =>
    !searchQuery ||
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Formations</h1>
          <p className="text-ink-soft mt-1">
            {isRestrictedUser ? 'Consultez vos formations' : 'Visualisez les formations par organisation'}
          </p>
        </div>
        <div className="text-sm text-ink-soft bg-cream-high px-3 py-1 rounded-full">
          {filteredCourses.length} formation{filteredCourses.length > 1 ? 's' : ''}
        </div>
      </div>

      {/* Organization Selector & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {!isRestrictedUser && (
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-ink-soft" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="bg-white border border-cream-high rounded-xl text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/20 px-3 py-2 w-auto"
            >
              <option value="">Sélectionner une organisation</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-soft" />
          <input
            type="text"
            placeholder="Rechercher une formation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30 pl-10 py-2 pr-4"
          />
        </div>
      </div>

      {/* Courses List */}
      {!selectedOrgId ? (
        <div className="bg-white shadow-ambient rounded-2xl text-center py-14 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
            <Building2 size={32} className="text-gold" />
          </div>
          <h3 className="text-lg font-semibold text-ink mb-1">
            {isRestrictedUser ? 'Aucune organisation associée' : 'Sélectionnez une organisation'}
          </h3>
          <p className="text-sm text-ink-soft max-w-sm mx-auto">
            {isRestrictedUser ? 'Contactez votre administrateur.' : 'Choisissez une organisation pour voir ses formations.'}
          </p>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="bg-white shadow-ambient rounded-2xl text-center py-14 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
            <GraduationCap size={32} className="text-gold" />
          </div>
          <h3 className="text-lg font-semibold text-ink mb-1">Aucune formation</h3>
          <p className="text-sm text-ink-soft max-w-sm mx-auto">Les formations se créent depuis le LMS.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <CourseCard key={course.id} course={course} onClick={() => setViewingCourse(course)} />
          ))}
        </div>
      )}

      {viewingCourse && (
        <CourseDetailsModal course={viewingCourse} onClose={() => setViewingCourse(null)} />
      )}
    </div>
  );
};

const CourseCard: React.FC<{ course: Course; onClick: () => void }> = ({ course, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white shadow-ambient rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-ambient-lg hover:-translate-y-1"
  >
    {course.thumbnailUrl ? (
      <div className="aspect-video w-full overflow-hidden bg-cream-high">
        <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
      </div>
    ) : (
      <div className="aspect-video w-full bg-gradient-to-br from-peach/30 to-coral/10 flex items-center justify-center">
        <GraduationCap className="w-12 h-12 text-coral/40" />
      </div>
    )}

    <div className="p-5">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-bold text-ink line-clamp-1">{course.title}</h3>
        <span className={`inline-flex ml-2 flex-shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.05em] ${
          course.isPublished
            ? 'bg-mint-container text-mint'
            : 'bg-cream-high text-ink-soft'
        }`}>
          {course.isPublished ? 'Publié' : 'Brouillon'}
        </span>
      </div>

      {course.description && (
        <p className="text-sm text-ink-soft mb-3 line-clamp-2">{course.description}</p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-cream-high">
        <div className="flex items-center gap-2 text-sm text-ink-soft">
          <Clock className="w-4 h-4" />
          <span>{formatDuration(course.estimatedDurationMinutes)}</span>
        </div>
        {course.certificateEnabled && (
          <span className="border border-cream-high text-ink hover:text-coral hover:bg-coral/5 rounded-full px-3 py-1 text-sm transition-colors cursor-pointer">
            Certificat
          </span>
        )}
      </div>
    </div>
  </div>
);

// ── Enrolled learner type for the detail modal ──
interface EnrolledLearner {
  learnerId: string;
  fullname: string;
  groupName: string;
  progressPercent: number;
  status: string;
  enrolledAt: string | null;
  completedAt: string | null;
}

type SortKey = 'fullname' | 'groupName' | 'progressPercent' | 'status' | 'enrolledAt' | 'completedAt';
type SortDir = 'asc' | 'desc';

const STATUS_ORDER: Record<string, number> = { not_started: 0, in_progress: 1, completed: 2 };

const CourseDetailsModal: React.FC<{ course: Course; onClose: () => void }> = ({ course, onClose }) => {
  const [enrolled, setEnrolled] = useState<EnrolledLearner[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('progressPercent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadEnrollmentData(); }, [course.id]);

  const loadEnrollmentData = async () => {
    try {
      const { data: enrollments, error } = await supabase
        .from('enrollments')
        .select('id, enrolled_at, learner_id, learners!inner(id, fullname), course_progress(progress_percent, completed_at)')
        .eq('course_id', course.id);

      if (error) throw error;
      if (!enrollments || enrollments.length === 0) { setEnrolled([]); setLoading(false); return; }

      const learnerIds = enrollments.map((e: { learner_id: string }) => e.learner_id);
      const { data: groupData } = await supabase
        .from('group_learners')
        .select('learner_id, groups(name)')
        .in('learner_id', learnerIds);

      const groupMap: Record<string, string[]> = {};
      if (groupData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const gl of groupData as any[]) {
          const name = gl.groups?.name as string | undefined;
          if (name) {
            if (!groupMap[gl.learner_id]) groupMap[gl.learner_id] = [];
            groupMap[gl.learner_id].push(name);
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: EnrolledLearner[] = enrollments.map((e: any) => {
        const learner = Array.isArray(e.learners) ? e.learners[0] : e.learners;
        const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
        const progressPercent: number = cp?.progress_percent ?? 0;
        const cpCompletedAt: string | null = cp?.completed_at ?? null;

        let status = 'not_started';
        if (cpCompletedAt || progressPercent >= 100) status = 'completed';
        else if (progressPercent > 0) status = 'in_progress';

        return {
          learnerId: e.learner_id,
          fullname: learner?.fullname || 'Inconnu',
          groupName: groupMap[e.learner_id]?.join(', ') || 'Aucun',
          progressPercent,
          status,
          enrolledAt: e.enrolled_at,
          completedAt: cpCompletedAt,
        };
      });

      setEnrolled(mapped);
    } catch (err) {
      console.error('Error loading enrollment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = q
      ? enrolled.filter(l => l.fullname.toLowerCase().includes(q) || l.groupName.toLowerCase().includes(q))
      : enrolled;

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'fullname':
        case 'groupName':
          cmp = a[sortKey].localeCompare(b[sortKey], 'fr');
          break;
        case 'progressPercent':
          cmp = a.progressPercent - b.progressPercent;
          break;
        case 'status':
          cmp = (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);
          break;
        case 'enrolledAt':
        case 'completedAt': {
          const da = a[sortKey] ? new Date(a[sortKey]!).getTime() : 0;
          const db = b[sortKey] ? new Date(b[sortKey]!).getTime() : 0;
          cmp = da - db;
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [enrolled, searchQuery, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'progressPercent' ? 'desc' : 'asc'); }
  };

  const total = enrolled.length;
  const completedCount = enrolled.filter(l => l.status === 'completed').length;
  const inProgress = enrolled.filter(l => l.status === 'in_progress').length;
  const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const formatDate = (dateStr: string | null) => dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '–';

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      not_started: 'bg-cream-high text-ink-soft',
      in_progress: 'bg-lavender-container text-lavender',
      completed:   'bg-mint-container text-mint',
    };
    const labels: Record<string, string> = {
      not_started: 'Non commencé',
      in_progress: 'En cours',
      completed:   'Terminé',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.not_started}`}>
        {labels[status] || status}
      </span>
    );
  };

  const progressBarColor = (pct: number) => pct >= 75 ? MINT : pct >= 50 ? GOLD : CORAL;

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-coral" />
      : <ChevronDown className="w-3 h-3 text-coral" />;
  };

  const thClass = 'px-4 py-2.5 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer select-none group';

  const isFiltered = searchQuery.trim().length > 0;

  const MIN_W = 600;
  const MIN_H = 400;
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalSize, setModalSize] = useState<{ w: number; h: number } | null>(null);
  const dragRef = useRef<{ edge: string; startX: number; startY: number; startW: number; startH: number } | null>(null);

  const onResizeStart = useCallback((edge: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { edge, startX: e.clientX, startY: e.clientY, startW: rect.width, startH: rect.height };

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      let newW = d.startW;
      let newH = d.startH;
      if (d.edge.includes('e')) newW = d.startW + dx * 2;
      if (d.edge.includes('w')) newW = d.startW - dx * 2;
      if (d.edge.includes('s')) newH = d.startH + dy * 2;
      if (d.edge.includes('n')) newH = d.startH - dy * 2;
      const maxW = window.innerWidth - 32;
      const maxH = window.innerHeight - 32;
      setModalSize({ w: Math.max(MIN_W, Math.min(newW, maxW)), h: Math.max(MIN_H, Math.min(newH, maxH)) });
    };

    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const Handle: React.FC<{ edge: string; className: string }> = ({ edge, className }) => (
    <div className={`absolute z-20 ${className}`} onMouseDown={(e) => onResizeStart(edge, e)} />
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-ambient relative"
        style={{
          width: modalSize ? modalSize.w : undefined,
          height: modalSize ? modalSize.h : undefined,
          maxWidth: modalSize ? undefined : '56rem',
          maxHeight: modalSize ? undefined : '90vh',
          ...(modalSize ? {} : { width: '100%' }),
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Handle edge="n"  className="top-0 left-3 right-3 h-1.5 cursor-ns-resize" />
        <Handle edge="s"  className="bottom-0 left-3 right-3 h-1.5 cursor-ns-resize" />
        <Handle edge="w"  className="left-0 top-3 bottom-3 w-1.5 cursor-ew-resize" />
        <Handle edge="e"  className="right-0 top-3 bottom-3 w-1.5 cursor-ew-resize" />
        <Handle edge="nw" className="top-0 left-0 w-3 h-3 cursor-nwse-resize" />
        <Handle edge="ne" className="top-0 right-0 w-3 h-3 cursor-nesw-resize" />
        <Handle edge="sw" className="bottom-0 left-0 w-3 h-3 cursor-nesw-resize" />
        <Handle edge="se" className="bottom-0 right-0 w-3 h-3 cursor-nwse-resize" />

        <div className="overflow-y-auto h-full">
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-cream-high px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-xl font-semibold text-ink truncate">{course.title}</h2>
              <span className={`inline-flex px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] rounded-full flex-shrink-0 ${
                course.isPublished ? 'bg-mint-container text-mint' : 'bg-cream-high text-ink-soft'
              }`}>
                {course.isPublished ? 'Publié' : 'Brouillon'}
              </span>
            </div>
            <button onClick={onClose} className="text-ink-soft hover:text-coral rounded-full flex-shrink-0 ml-4 p-1 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* KPI Bandeau */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-lavender-container">
                    <Users size={18} className="text-lavender" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : total}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Inscrits</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gold-container">
                    <TrendingUp size={18} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : inProgress}</p>
                    <p className="text-xs text-ink-soft mt-0.5">En cours</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-mint-container">
                    <CheckCircle size={18} className="text-mint" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : completedCount}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Terminées</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary-container">
                    <GraduationCap size={18} className="text-coral" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : `${completionRate}%`}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Taux de complétion</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Learners Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
              </div>
            ) : enrolled.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-3 text-cream-high" />
                <p className="text-ink-soft">Aucun apprenant inscrit à cette formation</p>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom ou groupe..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30 pl-9 pr-4 py-2 text-sm"
                  />
                </div>

                <div className="bg-white border border-cream-high rounded-xl overflow-hidden shadow-ambient">
                  <div className="px-5 py-3 border-b border-cream-high bg-cream-high/30 flex items-center justify-between">
                    <p className="text-sm font-medium text-ink">
                      {total} apprenant{total > 1 ? 's' : ''} inscrit{total > 1 ? 's' : ''}
                    </p>
                    {isFiltered && (
                      <p className="text-xs text-ink-soft">
                        {filteredAndSorted.length} résultat{filteredAndSorted.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-cream-high/30">
                        <tr>
                          <th className={thClass} onClick={() => handleSort('fullname')}>
                            <span className="flex items-center gap-1">Apprenant <SortIcon columnKey="fullname" /></span>
                          </th>
                          <th className={thClass} onClick={() => handleSort('groupName')}>
                            <span className="flex items-center gap-1">Groupe <SortIcon columnKey="groupName" /></span>
                          </th>
                          <th className={thClass} onClick={() => handleSort('progressPercent')}>
                            <span className="flex items-center gap-1">Progression <SortIcon columnKey="progressPercent" /></span>
                          </th>
                          <th className={thClass} onClick={() => handleSort('status')}>
                            <span className="flex items-center gap-1">Statut <SortIcon columnKey="status" /></span>
                          </th>
                          <th className={thClass} onClick={() => handleSort('enrolledAt')}>
                            <span className="flex items-center gap-1">Inscription <SortIcon columnKey="enrolledAt" /></span>
                          </th>
                          <th className={thClass} onClick={() => handleSort('completedAt')}>
                            <span className="flex items-center gap-1">Complétion <SortIcon columnKey="completedAt" /></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-cream-high">
                        {filteredAndSorted.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-sm text-ink-soft">
                              Aucun résultat pour "{searchQuery}"
                            </td>
                          </tr>
                        ) : (
                          filteredAndSorted.map((learner) => (
                            <tr key={learner.learnerId} className="hover:bg-cream/30">
                              <td className="px-4 py-3 text-sm font-semibold text-ink whitespace-nowrap">
                                {learner.fullname}
                              </td>
                              <td className="px-4 py-3 text-sm text-ink-soft whitespace-nowrap">
                                {learner.groupName}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[100px] overflow-hidden">
                                    <div
                                      className="h-full rounded-full transition-all duration-500"
                                      style={{
                                        width: `${learner.progressPercent}%`,
                                        backgroundColor: progressBarColor(learner.progressPercent),
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs text-ink-soft w-8 text-right">{learner.progressPercent}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">{statusBadge(learner.status)}</td>
                              <td className="px-4 py-3 text-sm text-ink-soft whitespace-nowrap">
                                {formatDate(learner.enrolledAt)}
                              </td>
                              <td className="px-4 py-3 text-sm text-ink-soft whitespace-nowrap">
                                {formatDate(learner.completedAt)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
